import { useEffect, useMemo, useRef, useState } from 'react';
import { getWebApp } from './max.js';
import {
  lookupVin, confirmVin, fetchProfile, saveProfile, fetchHistory, deleteHistory, sendFeedback,
  type VinResult, type VinSuccess, type HistoryItem,
} from './api.js';
import { VIN_PREFIXES, type VinPrefix, cleanTail, TAIL_MAX_LENGTH } from './utils.js';
import logoUrl from './assets/logo.png';

type PhoneStatus = 'pending' | 'granted' | 'denied';

export function App() {
  const webApp = useMemo(() => getWebApp(), []);
  const [prefix, setPrefix] = useState<VinPrefix>('X0T');
  const [tail, setTail] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<VinResult | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [phone, setPhone] = useState<string | null>(null);
  const [phoneStatus, setPhoneStatus] = useState<PhoneStatus>('pending');
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [repeat, setRepeat] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [commentOpen, setCommentOpen] = useState(false);
  const [pendingFallback, setPendingFallback] = useState<unknown>(null);
  const tailRef = useRef<HTMLInputElement>(null);

  const loadHistory = () => {
    const initData = webApp?.initData;
    if (!initData) return;
    fetchHistory(initData)
      .then((res) => setHistory(res.items ?? []))
      .catch(() => {});
  };

  const onSendFeedback = (requestId: string, answer: 'resolved' | 'unresolved') => {
    const initData = webApp?.initData;
    if (!initData) return;
    // Оптимистично обновляем локально, потом синхронизируем с сервером.
    setHistory((items) => items.map((it) => it.request_id === requestId ? { ...it, feedback: answer } : it));
    sendFeedback(initData, requestId, answer)
      .then(() => loadHistory())
      .catch(() => loadHistory());
  };

  const onDeleteHistory = (id: number) => {
    const initData = webApp?.initData;
    if (!initData) return;
    setHistory((items) => items.filter((it) => it.id !== id)); // оптимистично
    deleteHistory(initData, id)
      .then((res) => setHistory(res.items ?? []))
      .catch(() => loadHistory());
  };

  const confirmDelete = () => {
    if (confirmDeleteId !== null) onDeleteHistory(confirmDeleteId);
    setConfirmDeleteId(null);
  };

  useEffect(() => {
    tailRef.current?.focus();
    const initData = webApp?.initData;

    // Нет initData (открыто вне MAX) — работаем без телефона.
    if (!initData) {
      setPhoneStatus('granted');
      return;
    }

    loadHistory();

    const requestContact = () => {
      const contactPromise = webApp?.requestContact?.();
      if (!contactPromise) {
        // SDK без requestContact — разрешаем работу.
        setPhoneStatus('granted');
        return;
      }
      contactPromise
        .then((contact) => {
          if (contact?.phone) {
            setPhone(contact.phone);
            setPhoneStatus('granted');
            // Запоминаем телефон, чтобы не спрашивать в следующий раз.
            saveProfile(initData, contact.phone).catch(() => {});
          } else {
            setPhoneStatus('denied');
          }
        })
        .catch(() => setPhoneStatus('denied'));
    };

    // Сначала узнаём пользователя по max_user_id — вдруг телефон уже сохранён.
    fetchProfile(initData)
      .then((profile) => {
        if (profile.phone) {
          setPhone(profile.phone);
          setPhoneStatus('granted');
        } else {
          requestContact();
        }
      })
      .catch(() => requestContact());
  }, []);

  // Пока мини-апп открыт — подтягиваем статусы истории (например, "В работе"),
  // чтобы не ждать перезапуска. Опрос только когда приложение на экране.
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'visible') {
        loadHistory();
      }
    };
    const id = window.setInterval(refresh, 10000);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, []);

  const onTailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTail(cleanTail(e.target.value));
    setResult(null);
    setRepeat(false);
  };

  const runLookup = async (vin: string) => {
    if (!webApp?.initData) {
      setResult({ ok: false, error: { code: 'NO_INIT_DATA', message: 'Откройте мини-приложение из бота MAX.' } });
      return;
    }
    setLoading(true);
    try {
      const res = await lookupVin(vin, webApp.initData);
      setResult(res);
      webApp.HapticFeedback?.notificationOccurred(res.ok ? 'success' : 'error');
      // VIN найден — сразу открываем модал комментария. Получателя (менеджер
      // или чат из настроек) определит сервер при отправке.
      if (res.ok) {
        onRequestSend(buildFallback(res));
      }
    } catch (err) {
      setResult({
        ok: false,
        error: { code: 'NETWORK', message: err instanceof Error ? err.message : 'Сетевая ошибка' },
      });
      webApp?.HapticFeedback?.notificationOccurred('error');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (tail.length === 0) {
      setHint('Введите символы после префикса.');
      setResult(null);
      return;
    }
    setHint(null);
    setRepeat(false);
    void runLookup(prefix + tail);
  };

  // Повтор обращения из истории: подставляем VIN и запускаем обычный поиск.
  const repeatRequest = (vin: string) => {
    if (loading) return;
    const matched = VIN_PREFIXES.find((p) => vin.startsWith(p));
    if (matched) {
      setPrefix(matched);
      setTail(vin.slice(matched.length));
    } else {
      setTail(vin);
    }
    setHint(null);
    setRepeat(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    void runLookup(vin);
  };

  const onReset = () => {
    setResult(null);
    setTail('');
    setHint(null);
    setRepeat(false);
    setTimeout(() => tailRef.current?.focus(), 0);
  };

  // Открываем нижний модал для ввода комментария.
  const onRequestSend = (fallback: unknown) => {
    setPendingFallback(fallback);
    setComment('');
    setCommentOpen(true);
  };

  // Отправка уведомления в Bitrix (после ввода комментария в модале).
  const onSend = async (fallback: unknown, commentText: string) => {
    if (!webApp?.initData) return;
    setLoading(true);
    try {
      const res = await confirmVin(prefix + tail, webApp.initData, fallback, phone, commentText || null, repeat);
      webApp.HapticFeedback?.notificationOccurred(res.ok ? 'success' : 'error');
      setResult(res);
      if (res.ok) {
        setTail('');
        loadHistory();
      }
    } catch (err) {
      setResult({
        ok: false,
        error: { code: 'NETWORK', message: err instanceof Error ? err.message : 'Сетевая ошибка' },
      });
      webApp?.HapticFeedback?.notificationOccurred('error');
    } finally {
      setLoading(false);
    }
  };

  const submitComment = () => {
    setCommentOpen(false);
    void onSend(pendingFallback, comment);
  };

  return (
    <main className="page">
      <header className="page__header">
        <img className="page__logo" src={logoUrl} alt="" />
      </header>

      {phoneStatus === 'denied' ? (
        <p className="phone-denied">
          Для работы необходимо поделиться номером телефона. Перезапустите приложение и разрешите доступ к номеру.
        </p>
      ) : (
        <>
          <form className="form" onSubmit={onSubmit}>
            <label className="form__label" htmlFor="vin-tail">
              Укажите VIN
            </label>
            <div className="form__vin-row">
              <select
                className="form__prefix"
                value={prefix}
                onChange={(e) => setPrefix(e.target.value as VinPrefix)}
                disabled={loading || phoneStatus === 'pending'}
                aria-label="Префикс VIN"
              >
                {VIN_PREFIXES.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <input
                ref={tailRef}
                id="vin-tail"
                className="form__input form__tail"
                type="text"
                inputMode="text"
                autoComplete="off"
                autoCapitalize="characters"
                spellCheck={false}
                maxLength={TAIL_MAX_LENGTH}
                value={tail}
                onChange={onTailChange}
                disabled={loading || phoneStatus === 'pending'}
              />
            </div>
            {hint ? <p className="form__hint">{hint}</p> : null}
            {result?.ok !== true && (
              <button
                className="form__submit"
                type="submit"
                disabled={loading || tail.length === 0 || phoneStatus === 'pending'}
              >
                {loading ? 'Ищем…' : phoneStatus === 'pending' ? 'Ожидание…' : 'Найти'}
              </button>
            )}
          </form>

          {result ? (
            <ResultCard result={result} loading={loading} onRequestSend={onRequestSend} onReset={onReset} />
          ) : null}

          {history.length > 0 && (
            <section className="history">
              <h2 className="history__title">
                История обращений
                <span className="history__hint">смахните запись до края, чтобы удалить</span>
              </h2>
              <ul className="history__list">
                {history.map((item) => (
                  <HistoryRow
                    key={item.id}
                    item={item}
                    loading={loading}
                    pendingDelete={confirmDeleteId === item.id}
                    onRepeat={repeatRequest}
                    onDelete={setConfirmDeleteId}
                    onFeedback={onSendFeedback}
                  />
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      {confirmDeleteId !== null && (
        <div className="modal-overlay">
          <div className="modal">
            <h2 className="modal__title">Удалить запись из истории?</h2>
            <div className="modal__actions">
              <button className="modal__cancel" onClick={() => setConfirmDeleteId(null)}>
                Отмена
              </button>
              <button className="modal__danger" onClick={confirmDelete}>
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}

      {commentOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <h2 className="modal__title">Опишите Вашу проблему</h2>
            <textarea
              className="modal__textarea"
              placeholder="Необязательно"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              autoFocus
            />
            <div className="modal__actions">
              <button className="modal__cancel" onClick={() => setCommentOpen(false)} disabled={loading}>
                Отмена
              </button>
              <button className="modal__submit" onClick={submitComment} disabled={loading}>
                Отправить
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}

function formatDate(s: string): string {
  // "2026-05-25 12:13:45" → "25.05.2026 12:13"
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(s);
  if (!m) return s;
  return `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}`;
}

const SWIPE_MAX = 120;      // максимальный сдвиг карточки, px
const SWIPE_TRIGGER = 90;   // доведён до края — спрашиваем удаление; иначе возврат

function HistoryRow({
  item,
  loading,
  pendingDelete,
  onRepeat,
  onDelete,
  onFeedback,
}: {
  item: HistoryItem;
  loading: boolean;
  pendingDelete: boolean;
  onRepeat: (vin: string) => void;
  onDelete: (id: number) => void;
  onFeedback: (requestId: string, answer: 'resolved' | 'unresolved') => void;
}) {
  const [dx, setDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startX = useRef(0);

  // Пока открыт диалог подтверждения по этой записи — держим карточку отъехавшей.
  // Диалог закрыт (отмена) — возвращаем на место.
  useEffect(() => {
    if (!pendingDelete) setDx(0);
  }, [pendingDelete]);

  const onTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    startX.current = touch.clientX;
    setDragging(true);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    if (!touch) return;
    let next = touch.clientX - startX.current;
    if (next > SWIPE_MAX) next = SWIPE_MAX;
    if (next < -SWIPE_MAX) next = -SWIPE_MAX;
    setDx(next);
  };

  const onTouchEnd = () => {
    setDragging(false);
    if (Math.abs(dx) >= SWIPE_TRIGGER) {
      // Доведён до края — фиксируем карточку открытой и открываем подтверждение.
      setDx(dx < 0 ? -SWIPE_MAX : SWIPE_MAX);
      onDelete(item.id);
    } else {
      setDx(0); // недоведён — возврат на место
    }
  };

  const armed = Math.abs(dx) >= SWIPE_TRIGGER;

  return (
    <li className="history__item">
      <div className={armed ? 'history__delete history__delete--armed' : 'history__delete'} aria-hidden="true">
        <span>Удалить</span>
        <span>Удалить</span>
      </div>
      <div
        className="history__card"
        style={{ transform: `translateX(${dx}px)`, transition: dragging ? 'none' : 'transform .18s ease' }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <div className="history__row">
          <span className="history__vin">{item.request_no ? `№${item.request_no} · ${item.vin}` : item.vin}</span>
          <span className="history__date">{formatDate(item.created_at)}</span>
        </div>
        <span className={'history__status' + (
          item.status === 'in_progress' ? ' history__status--work'
          : item.status === 'done' && item.feedback === 'resolved' ? ' history__status--done'
          : item.status === 'done' && item.feedback === 'unresolved' ? ' history__status--warn'
          : item.status === 'done' ? ' history__status--done'
          : ''
        )}>
          {item.status === 'in_progress' ? 'В работе, скоро с Вами свяжутся'
            : item.status === 'done' && item.feedback === 'resolved' ? 'Завершён · проблема решена'
            : item.status === 'done' && item.feedback === 'unresolved' ? 'Завершён · проблема не решена'
            : item.status === 'done' ? 'Завершён'
            : 'Запрос отправлен'}
        </span>
        {item.status === 'done' && item.feedback === null && (
          <div className="history__feedback">
            <p className="history__feedback-q">Ваша проблема решена?</p>
            <div className="history__feedback-row">
              <button
                className="history__feedback-btn history__feedback-btn--yes"
                onClick={() => onFeedback(item.request_id, 'resolved')}
                disabled={loading}
              >Да</button>
              <button
                className="history__feedback-btn history__feedback-btn--no"
                onClick={() => onFeedback(item.request_id, 'unresolved')}
                disabled={loading}
              >Нет</button>
            </div>
          </div>
        )}
        {item.comment ? <p className="history__comment">{item.comment}</p> : null}
        <button
          className="history__repeat"
          onClick={() => onRepeat(item.vin)}
          disabled={loading}
        >
          Сообщить о новой проблеме
        </button>
      </div>
    </li>
  );
}

function ResultCard({
  result,
  loading,
  onRequestSend,
  onReset,
}: {
  result: VinResult;
  loading: boolean;
  onRequestSend: (fallback: unknown) => void;
  onReset: () => void;
}) {
  if (!result.ok) {
    // VIN не найден, но запрос можно отправить (есть fallback) — не критично,
    // показываем как нейтральное предупреждение, а не красную "Ошибку".
    if (result.fallback !== undefined) {
      return (
        <section className="result result--warn">
          <p className="result__warn-text">{result.error.message}</p>
          <button
            className="result__confirm"
            onClick={() => onRequestSend(result.fallback)}
            disabled={loading}
          >
            {loading ? 'Отправляем…' : 'VIN точно введён корректно'}
          </button>
        </section>
      );
    }

    // Настоящая ошибка без возможности отправки.
    return (
      <section className="result result--error">
        <h2>Ошибка</h2>
        <p>{result.error.message}</p>
      </section>
    );
  }

  // notified_via != null — отправка уже выполнялась (lookup возвращает null).
  // Получателя определяет сервер (менеджер или чат из настроек), поэтому
  // на этапе lookup всегда даём отправить.
  const sent = result.notified_via != null;

  if (!sent) {
    return (
      <section className="result result--ok">
        <p className="result__notice result__notice--ok">VIN номер корректный.</p>
        <button
          className="result__confirm"
          onClick={() => onRequestSend(buildFallback(result))}
          disabled={loading}
        >
          {loading ? 'Отправляем…' : 'Отправить запрос'}
        </button>
      </section>
    );
  }

  // Финальное состояние: запрос отправлен либо получателя нет — показываем "Добавить ещё".
  // VIN не дублируем: он виден в истории (после отправки) или в поле формы.
  return (
    <section className="result result--ok">
      <p className="result__notice">{renderNotice(result)}</p>
      <button className="result__reset" onClick={onReset}>
        Добавить ещё
      </button>
    </section>
  );
}

/** Собирает fallback-объект из успешного lookup-ответа для передачи в confirm.php */
function buildFallback(result: VinSuccess): unknown {
  return {
    manager: result.manager,
    chat: result.chat ?? null,
    counterparty: result.counterparty ?? null,
    data: result.data,
  };
}

function renderNotice(result: VinSuccess): string {
  // Получателя нет (ни менеджера, ни чата в настройках) — отправлять было некому.
  if (result.notified_via === 'none') return 'Получатель не определён — уведомление не отправлено.';
  // Попытка отправки была. Даже если Bitrix её отклонил — пользователю показываем успех,
  // детали ошибки доставки остаются в серверном логе.
  return 'Запрос отправлен.';
}
